import AdminPageLoader from "components/AdminPageLoader";
import { useState } from "react";
import { DataTable } from "components/PortalDataTable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import BooksFilter from "admin/AllBooks/BooksFilter";
import useBulkActionBooks from "api/BookResource/useBulkActionBooks";
import useFetchPublisherBooks from "api/BookResource/useFetchPublisherBooks";
import { CustomPaginator } from "components";
import { useFormik } from "formik";
import Admin from "layouts/Admin";
import { BsFillBookFill } from "react-icons/bs";
import useBooksFilterStore from "store/booksFilterStore";
import AddBook from "./AddBook";
import DeleteBook from "./DeleteBook";
import EditBook from "./EditBook";
import ImportBooks from "./ImportBooks";
import BookCoverPreview from "components/BookCoverPreview";
import { activeCurrencies } from "utils";

const Books = () => {
	const [firstPage, setFirstPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	const [expandedRows, setExpandedRows] = useState(null);
	const { bookFilters, page, setPage } = useBooksFilterStore();

	const { data: booksData, isLoading: booksIsLoading } = useFetchPublisherBooks({
		page: page,
		rows_per_page: rowsPerPage,
		...bookFilters,
	});

	const [selectedBooks, setSelectedBooks] = useState([]);

	const { mutateAsync: mutateBulkAction, isLoading: isActioning } =
		useBulkActionBooks();

	const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
		initialValues: {
			books: [],
			action: "",
		},

		onSubmit: async (data) => {
			const bookIds = data.books.map((book) => book.id);
			switch (data.action) {
				case "Activate":
					await mutateBulkAction({
						books: bookIds,
						action: "activate",
					});
					break;

				case "Deactivate":
					await mutateBulkAction({
						books: bookIds,
						action: "deactivate",
					});
					break;

				case "Set as free":
					await mutateBulkAction({
						books: bookIds,
						action: "setAsFree",
					});
					break;

				case "Set as premium":
					await mutateBulkAction({
						books: bookIds,
						action: "setAsPremium",
					});
					break;

				case "Delete":
					await mutateBulkAction({
						books: bookIds,
						action: "deletePermanently",
					});
					break;

				default:
					break;
			}
			setSelectedBooks([]);
			setFieldValue("books", []);
		},
		enableReinitialize: true,
	});

	const booksList = booksData?.data?.records || [];
	const totalRecords = booksData?.data?.total_records || 0;

	const handleSelectionChange = (e) => {
		setSelectedBooks(e.value);
		setFieldValue("books", e.value);
	};

	// Column Templates
	const bookCoverTemplate = (rowData) => {
		return (
			<BookCoverPreview
				coverUrl={rowData?.book_cover?.url}
				title={rowData?.title}
				thumbnailWidth={56}
				thumbnailHeight={70}
			/>
		);
	};

	const titleTemplate = (rowData) => {
		return (
			<div className="flex flex-col">
				<span className="font-semibold text-gray-900">{rowData?.title}</span>
				{rowData?.isbn && (
					<span className="text-xs text-gray-400">ISBN: {rowData?.isbn}</span>
				)}
			</div>
		);
	};

	const authorTemplate = (rowData) => {
		const publisher =
			rowData?.user?.publishers?.[0]?.name ||
			`${rowData?.user?.first_name ?? ""} ${rowData?.user?.last_name ?? ""}`;
		return (
			<div className="flex flex-col">
				<span className="text-sm text-gray-700">{rowData?.author}</span>
				<span className="text-xs text-gray-400 truncate max-w-xs">
					{publisher}
				</span>
			</div>
		);
	};

	const categoryTemplate = (rowData) => {
		return (
			<span className="text-sm text-gray-600">
				{rowData?.category?.name || "N/A"}
			</span>
		);
	};

	const priceTemplate = (rowData) => {
		return (
			<span className="font-semibold">
				{rowData?.currency_code}
				{rowData?.currency_symbol}
				{rowData?.price}
			</span>
		);
	};

	const statusTemplate = (rowData) => {
		return (
			<Tag
				value={rowData?.is_active ? "Active" : "Inactive"}
				severity={rowData?.is_active ? "success" : "warning"}
				className="text-xs"
			/>
		);
	};

	const actionsTemplate = (rowData) => {
		return (
			<div className="flex items-center gap-1">
				<EditBook elementId={`editbook-${rowData.id}`} book={rowData} />
				<DeleteBook elementId={`book-${rowData.id}`} bookId={rowData.id} />
			</div>
		);
	};

	// Row expansion template
	const rowExpansionTemplate = (data) => {
		const publisher =
			data?.user?.publishers?.[0]?.name ||
			`${data?.user?.first_name ?? ""} ${data?.user?.last_name ?? ""}`;
		return (
			<div className="p-4 bg-gray-50 border-t border-gray-200">
				<Card className="shadow-none border-0">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						{/* Book Details */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Book Details</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium text-gray-500">ISBN:</span>{" "}
									<span className="text-gray-800">{data?.isbn || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Publisher:</span>{" "}
									<span className="text-gray-800">{publisher}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Category:</span>{" "}
									<span className="text-gray-800">{data?.category?.name || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Genre:</span>{" "}
									<span className="text-gray-800">{data?.genres?.[0]?.name || "N/A"}</span>
								</div>
							</div>
						</div>

						{/* Pricing & Status */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Pricing & Status</h4>
							<div className="space-y-2 text-sm">
								<div className="flex items-center gap-2">
									<span className="font-medium text-gray-500">Status:</span>
									<Tag
										value={data?.is_active ? "Active" : "Inactive"}
										severity={data?.is_active ? "success" : "danger"}
									/>
								</div>
								<div>
									<span className="font-medium text-gray-500">Price ({activeCurrencies.USD.label}):</span>{" "}
									<span className="text-gray-800">${data?.price || 0}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Price ({activeCurrencies.ZWL.label}):</span>{" "}
									<span className="text-gray-800">${data?.zwl_price || 0}</span>
								</div>
								<div className="flex items-center gap-2">
									<span className="font-medium text-gray-500">Free:</span>
									<Tag
										value={data?.isFree ? "Yes" : "No"}
										severity={data?.isFree ? "success" : "secondary"}
									/>
								</div>
							</div>
						</div>

						{/* Availability */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Availability</h4>
							<div className="space-y-2 text-sm">
								<div className="flex items-center gap-2">
									<span className="font-medium text-gray-500">Sellable:</span>
									<Tag
										value={data?.sellable ? "Yes" : "No"}
										severity={data?.sellable ? "success" : "secondary"}
									/>
								</div>
								<div className="flex items-center gap-2">
									<span className="font-medium text-gray-500">On Subscription:</span>
									<Tag
										value={data?.subscription ? "Yes" : "No"}
										severity={data?.subscription ? "success" : "secondary"}
									/>
								</div>
							</div>
						</div>
					</div>
				</Card>
			</div>
		);
	};

	if (booksIsLoading) return <AdminPageLoader />;

	return (
		<Admin
			pageTitle="My Books"
			headerContent={
				<>
					<AddBook />
					<ImportBooks />
				</>
			}
			filters={<BooksFilter />}
		>
				<div className="space-y-4">
					{/* Bulk Actions */}
					{selectedBooks.length > 0 && (
						<form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-4 bg-akgreen bg-opacity-5 p-3 rounded-xl border border-akgreen border-opacity-20">
							<span className="text-sm font-bold text-akgreener">
								{selectedBooks.length} book(s) selected
							</span>

							<select
								name="action"
								className="px-3 py-2 border border-gray-200 rounded-lg shadow-sm bg-white"
								value={values.action}
								onChange={handleChange}
							>
								<option label="-- Select action --"></option>
								<option value="Delete">Permanently delete</option>
								<option value="Set as free">Set as free</option>
								<option value="Set as premium">Set as premium</option>
								<option value="Activate">Activate</option>
								<option value="Deactivate">Deactivate</option>
							</select>

							{values.action && (
								<button
									type="submit"
									className="px-4 py-2 bg-akgreen text-white rounded-lg font-semibold flex gap-2 items-center justify-center hover:bg-akgreenest transition-colors"
								>
									Execute{" "}
									{isActioning && (
										<BsFillBookFill
											size={18}
											className="animate-spin text-white"
										/>
									)}
								</button>
							)}
						</form>
					)}

					{/* DataTable */}
					<DataTable
						value={booksList}
						selection={selectedBooks}
						onSelectionChange={handleSelectionChange}
						selectionMode="checkbox"
						expandedRows={expandedRows}
						onRowToggle={(e) => setExpandedRows(e.data)}
						rowExpansionTemplate={rowExpansionTemplate}
						dataKey="id"
						tableClassName="font-sans text-sm"
						loading={booksIsLoading}
						emptyMessage="No books found"
					>
						<Column
							selectionMode="multiple"
							headerStyle={{ width: "3rem" }}
						/>
						<Column
							expander
							style={{ width: "3rem" }}
						/>
						<Column
							header="Cover"
							body={bookCoverTemplate}
							style={{ width: "70px" }}
							className="text-center"
						/>
						<Column
							header="Title"
							body={titleTemplate}
							style={{ minWidth: "180px" }}
						/>
						<Column
							header="Author"
							body={authorTemplate}
							style={{ minWidth: "150px" }}
						/>
						<Column
							header="Category"
							body={categoryTemplate}
							style={{ width: "110px" }}
						/>
						<Column
							header="Price"
							body={priceTemplate}
							style={{ width: "100px" }}
						/>
						<Column
							header="Status"
							body={statusTemplate}
							style={{ width: "90px" }}
							className="text-center"
						/>
						<Column
							header="Actions"
							body={actionsTemplate}
							style={{ width: "100px" }}
							className="text-center"
						/>
					</DataTable>

					{/* Pagination */}
					<div className="flex justify-center bg-gray-200 border-r border-l border-gray-400 rounded-full mt-4">
						<CustomPaginator
							firstPage={firstPage}
							rowsPerPage={rowsPerPage}
							setFirstPage={setFirstPage}
							setRowsPerPage={setRowsPerPage}
							totalRecords={totalRecords}
							setPage={setPage}
						/>
					</div>
				</div>
		</Admin>
	);
};

export default Books;
